"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { animate, AnimatePresence, motion, useMotionValue } from "framer-motion";
import { useTranslation } from "react-i18next";

const SHEET_DISMISS_MIN_DRAG_PX = 72;
const SHEET_DISMISS_MAX_DRAG_PX = 120;
const SHEET_DISMISS_DRAG_RATIO = 0.16;
const SHEET_DISMISS_FLICK_MIN_DRAG_PX = 12;
const SHEET_DISMISS_VELOCITY_Y = 650;
const FILTER_TOP_FADE_PX = 24;
const FILTER_BOTTOM_FADE_PX = 24;

export const shouldDismissEventFilterSheet = ({
  offsetY,
  velocityY,
  sheetHeight,
}) => {
  const distanceThreshold = Math.min(
    SHEET_DISMISS_MAX_DRAG_PX,
    Math.max(SHEET_DISMISS_MIN_DRAG_PX, sheetHeight * SHEET_DISMISS_DRAG_RATIO)
  );

  return (
    offsetY >= distanceThreshold ||
    (offsetY >= SHEET_DISMISS_FLICK_MIN_DRAG_PX &&
      velocityY >= SHEET_DISMISS_VELOCITY_Y)
  );
};

function EventFilterSheet({
  children,
  title,
  onClose,
  closeLabel,
  footer,
  contentClassName = "",
  expandedHeader = false,
}) {
  const { t } = useTranslation("events");
  const [open, setOpen] = useState(true);
  const [topFadePx, setTopFadePx] = useState(0);
  const dragY = useMotionValue(0);
  const sheetRef = useRef(null);
  const closeCallbackRef = useRef(null);
  const closeRequestedRef = useRef(false);
  const dragStateRef = useRef(null);
  const didDragRef = useRef(false);
  const snapAnimationRef = useRef(null);
  const scrollMask = topFadePx > 0
    ? `linear-gradient(to bottom, transparent 0px, #000 ${topFadePx}px, #000 calc(100% - ${FILTER_BOTTOM_FADE_PX}px), transparent 100%)`
    : `linear-gradient(to bottom, #000 0px, #000 calc(100% - ${FILTER_BOTTOM_FADE_PX}px), transparent 100%)`;

  useEffect(() => {
    if (window.matchMedia?.("(min-width: 1024px)").matches) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      snapAnimationRef.current?.stop();
    };
  }, []);

  const requestClose = useCallback(
    (afterClose) => {
      if (closeRequestedRef.current) return;
      closeRequestedRef.current = true;
      closeCallbackRef.current = afterClose || onClose;
      setOpen(false);
    },
    [onClose]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        requestClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [requestClose]);

  const handleExitComplete = () => {
    if (!closeRequestedRef.current) return;
    const callback = closeCallbackRef.current || onClose;
    callback?.();
  };

  const snapSheetBack = useCallback(() => {
    snapAnimationRef.current?.stop();
    snapAnimationRef.current = animate(dragY, 0, {
      type: "spring",
      stiffness: 520,
      damping: 42,
      mass: 0.7,
    });
  }, [dragY]);

  const beginDrag = (inputType, inputId, clientY, timeStamp) => {
    if (closeRequestedRef.current) return;

    snapAnimationRef.current?.stop();
    didDragRef.current = false;
    dragStateRef.current = {
      inputType,
      inputId,
      startY: clientY,
      lastY: clientY,
      lastAt: timeStamp,
      velocityY: 0,
    };
  };

  const moveDrag = (inputType, inputId, clientY, timeStamp) => {
    const dragState = dragStateRef.current;
    if (
      !dragState ||
      dragState.inputType !== inputType ||
      dragState.inputId !== inputId
    ) {
      return;
    }

    const nextY = Math.max(0, clientY - dragState.startY);
    const elapsed = Math.max(1, timeStamp - dragState.lastAt);
    dragState.velocityY = ((clientY - dragState.lastY) / elapsed) * 1000;
    dragState.lastY = clientY;
    dragState.lastAt = timeStamp;

    if (nextY > 2) didDragRef.current = true;
    dragY.set(nextY);
  };

  const finishDrag = (inputType, inputId, timeStamp, cancelled = false) => {
    const dragState = dragStateRef.current;
    if (
      !dragState ||
      dragState.inputType !== inputType ||
      dragState.inputId !== inputId
    ) {
      return;
    }

    dragStateRef.current = null;
    const offsetY = dragY.get();
    const releaseVelocityY =
      timeStamp - dragState.lastAt > 80 ? 0 : dragState.velocityY;
    const shouldDismiss = shouldDismissEventFilterSheet({
      offsetY,
      velocityY: cancelled ? 0 : releaseVelocityY,
      sheetHeight: sheetRef.current?.offsetHeight || 0,
    });

    if (!cancelled && shouldDismiss) {
      requestClose();
    } else {
      snapSheetBack();
    }
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === "touch") return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    beginDrag("pointer", event.pointerId, event.clientY, event.timeStamp);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (event.pointerType === "touch") return;
    moveDrag("pointer", event.pointerId, event.clientY, event.timeStamp);
    if (didDragRef.current) event.preventDefault();
  };

  const handlePointerEnd = (event, cancelled = false) => {
    if (event.pointerType === "touch") return;
    finishDrag("pointer", event.pointerId, event.timeStamp, cancelled);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    beginDrag("touch", touch.identifier, touch.clientY, event.timeStamp);
  };

  const handleTouchMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.inputType !== "touch") return;

    const touch = Array.from(event.touches || []).find(
      (candidate) => candidate.identifier === dragState.inputId
    );
    if (!touch) return;

    moveDrag("touch", touch.identifier, touch.clientY, event.timeStamp);
    if (didDragRef.current) event.preventDefault();
  };

  const handleTouchEnd = (event, cancelled = false) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.inputType !== "touch") return;

    const touch = Array.from(event.changedTouches || []).find(
      (candidate) => candidate.identifier === dragState.inputId
    );
    if (!touch) return;

    finishDrag("touch", touch.identifier, event.timeStamp, cancelled);
  };

  const renderedFooter =
    typeof footer === "function" ? footer({ closeSheet: requestClose }) : footer;

  const sheetVariants = {
    // Keep the glass surface fully rendered throughout the movement. Fading the
    // transformed backdrop-filter layer makes Safari show the page through the
    // sheet until the opening animation has settled.
    hidden: { y: "100%", opacity: 1 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.34,
        ease: [0.22, 1, 0.36, 1],
      },
    },
    exit: {
      y: "112%",
      opacity: 1,
      transition: {
        duration: 0.26,
        ease: [0.32, 0, 0.67, 0],
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.18, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.18, ease: "easeIn" },
    },
  };

  const content = (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[40] flex items-end justify-center pointer-events-auto lg:items-center lg:pl-20 lg:bg-black/20"
          initial="hidden"
          animate="visible"
          exit="exit"
          role="presentation"
        >
          <motion.button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label={closeLabel || t("filters.sheet.close", { defaultValue: "Close" })}
            variants={backdropVariants}
            onClick={() => requestClose()}
          />
          <motion.div
            className={`relative w-full max-w-none will-change-transform [@media(orientation:landscape)]:max-w-[402px] lg:h-[min(724px,calc(100dvh-40px))] lg:max-h-[min(724px,calc(100dvh-40px))] lg:max-w-[402px] ${expandedHeader ? "h-[calc(100dvh-var(--vg-safe-area-top)-136px)] max-h-[calc(100dvh-var(--vg-safe-area-top)-136px)]" : "h-[calc(100dvh-var(--vg-safe-area-top)-102px)] max-h-[calc(100dvh-var(--vg-safe-area-top)-102px)]"}`}
            variants={sheetVariants}
          >
            <motion.section
              ref={sheetRef}
              role="dialog"
              aria-modal="true"
              className="relative h-full w-full overflow-hidden rounded-t-[45px] bg-gradient-to-r from-[rgba(25,25,25,0.7)] to-[rgba(27,45,97,0.7)] text-white shadow-[0_-24px_70px_rgba(0,0,0,0.24)] backdrop-blur-[37.5px] lg:rounded-[45px] lg:from-[#272c3c] lg:to-[#202f55] lg:backdrop-blur-none"
              style={{ y: dragY }}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="relative block w-full touch-none cursor-grab select-none bg-transparent px-[30px] pt-[14px] text-center active:cursor-grabbing"
                aria-label={closeLabel || t("filters.sheet.close", { defaultValue: "Close" })}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={(event) => handlePointerEnd(event)}
                onPointerCancel={(event) => handlePointerEnd(event, true)}
                onLostPointerCapture={(event) => handlePointerEnd(event)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={(event) => handleTouchEnd(event)}
                onTouchCancel={(event) => handleTouchEnd(event, true)}
                onClick={() => {
                  if (!didDragRef.current) requestClose();
                }}
              >
                <div className="mx-auto h-[5px] w-[100px] rounded-[35px] bg-white/25" />
                <div className="relative mt-[10px] flex h-6 items-center justify-center">
                  <p className="text-[14px] font-medium leading-[22px] tracking-[-0.56px] text-white/50">
                    {t("filters.sheet.swipe_down", { defaultValue: "Swipe down to close" })}
                  </p>
                </div>
              </button>

              <div
                data-event-filter-scroll
                data-testid="event-filter-scroll"
                onScroll={(event) => {
                  const nextFade = Math.min(FILTER_TOP_FADE_PX, Math.max(0, Math.round(event.currentTarget.scrollTop)));
                  setTopFadePx((previous) => previous === nextFade ? previous : nextFade);
                }}
                style={{ maskImage: scrollMask, WebkitMaskImage: scrollMask }}
                className={`absolute inset-x-0 bottom-[calc(var(--bottom-nav-clearance,112px)+39px)] top-[62px] overflow-y-auto px-[30px] pb-6 custom-scrollbar lg:bottom-[79px] ${contentClassName}`}
              >
                {title ? (
                  <h2 className="mb-[25px] text-[24px] font-semibold leading-[22px] tracking-[-0.4px] text-white">
                    {title}
                  </h2>
                ) : null}
                {children}
              </div>

              {renderedFooter ? (
                <footer className="absolute inset-x-0 bottom-[calc(var(--bottom-nav-clearance,112px)-16px)] px-[30px] lg:bottom-6">
                  {renderedFooter}
                </footer>
              ) : null}
            </motion.section>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return null;
  return createPortal(content, document.body);
}

export default EventFilterSheet;
